import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Bookmark,
  Heart,
  Lock,
  LogIn,
  MessageCircle,
  PencilLine,
  Reply,
  ShieldCheck,
  UserPlus,
  X,
} from "lucide-react";

const actionMessages = {
  comment: {
    badge: "Community",
    title: "Sign in to join the discussion",
    description:
      "A quick sign in keeps the conversation constructive and lets you reply instantly.",
  },
  like: {
    badge: "Engagement",
    title: "Sign in to show appreciation",
    description:
      "Support the stories you care about and keep your favorites in one place.",
  },
  bookmark: {
    badge: "Library",
    title: "Save insights for later",
    description:
      "Create an account to build a personal reading list across the community.",
  },
  reply: {
    badge: "Conversation",
    title: "Sign in to reply",
    description:
      "Pick up the thread with a free account and stay notified about follow-ups.",
  },
  report: {
    badge: "Safety",
    title: "Help keep the space safe",
    description:
      "We review every report. Sign in so we can follow up and keep the community healthy.",
  },
  edit: {
    badge: "Your work",
    title: "Sign in to make updates",
    description:
      "Manage your contributions confidently with quick access to editing tools.",
  },
  continue: {
    badge: "Community",
    title: "Sign in to continue",
    description:
      "Unlock tailored recommendations, follow topics, and collaborate with peers.",
  },
  follow: {
    badge: "Connections",
    title: "Sign in to follow community members",
    description:
      "Follow community members and stay connected with what they share.",
  },
};

const iconLookup = {
  comment: MessageCircle,
  like: Heart,
  bookmark: Bookmark,
  reply: Reply,
  report: ShieldCheck,
  edit: PencilLine,
  continue: Lock,
  follow: UserPlus,
};

const getConfig = (action) => {
  const message = actionMessages[action] || actionMessages.continue;
  const Icon = iconLookup[action] || iconLookup.continue;
  return {
    ...actionMessages.continue,
    ...message,
    icon: Icon,
  };
};

export default function LoginPrompt({
  action = "continue",
  inline = false,
  onClose,
}) {
  const config = getConfig(action);
  const Icon = config.icon;

  if (inline) {
    return (
      <section className="relative overflow-hidden rounded-xl border border-slate-200 bg-white/95 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-[#161616]/95">
        <div
          className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-sky-500/5 to-transparent dark:from-indigo-500/10 dark:via-[#161616]/60"
          aria-hidden
        />
        {/* <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-sky-500 to-blue-500" aria-hidden /> */}
        <div className="relative flex flex-col gap-6 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#3db8d9]/10 text-primary dark:bg-[#3db8d9]/20 dark:text-primary/70">
                <Icon className="h-6 w-6" strokeWidth={1.8} />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide text-primary dark:text-primary/70">
                {config.badge}
              </span>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
              {config.title}
            </h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {config.description}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-60">
            <Link
              to="/community/login"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-sky-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:from-indigo-700 hover:to-sky-700"
            >
              <LogIn className="h-4 w-4" strokeWidth={1.8} />
              Sign In
            </Link>
            <Link
              to="/community/register"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-indigo-300 hover:text-primary dark:border-gray-800 dark:text-slate-200 dark:hover:border-primary dark:hover:text-primary/70"
            >
              <UserPlus className="h-4 w-4" strokeWidth={1.8} />
              Create Account
            </Link>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-[#0E0E0E] dark:hover:text-slate-200"
              type="button"
            >
              <span className="sr-only">Close</span>
              <X className="h-4 w-4" strokeWidth={1.8} />
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 px-4 backdrop-blur">
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-gray-800 dark:bg-[#161616]">
        {/* <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-sky-500 to-blue-500" aria-hidden /> */}
        <div className="space-y-6 px-7 pb-8 pt-9">
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#3db8d9]/10 text-primary dark:bg-[#3db8d9]/20 dark:text-primary/70">
              <Icon className="h-8 w-8" strokeWidth={1.8} />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary dark:text-primary/70">
                {config.badge}
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
                {config.title}
              </h2>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {config.description}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Link
              to="/community/login"
              className="flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-sky-600 px-5 py-3 text-sm font-semibold text-white transition-all hover:from-indigo-700 hover:to-sky-700"
            >
              <LogIn className="h-4 w-4" strokeWidth={1.8} />
              Sign In to Continue
            </Link>
            <Link
              to="/community/register"
              className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-indigo-300 hover:text-primary dark:border-gray-800 dark:text-slate-200 dark:hover:border-primary dark:hover:text-primary/70"
            >
              <UserPlus className="h-4 w-4" strokeWidth={1.8} />
              Create a Free Account
            </Link>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-[#0E0E0E] dark:hover:text-slate-200"
            type="button"
          >
            <span className="sr-only">Close</span>
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        )}
      </div>
    </div>
  );
}

LoginPrompt.propTypes = {
  action: PropTypes.oneOf([
    "comment",
    "like",
    "bookmark",
    "reply",
    "report",
    "edit",
    "continue",
    "follow",
  ]),
  inline: PropTypes.bool,
  onClose: PropTypes.func,
};
